/**
 * src/utils/exportStatement.ts
 * Export utilities for CKAudit v2 financial ledger
 * Supports RFC-4180 UTF-8 CSV with Excel BOM prefix and printable statement triggers.
 */

import type { TransactionItem } from '../services/api';

/**
 * Escapes a field value for CSV according to RFC-4180
 */
export function escapeCsvField(field: any): string {
  if (field === null || field === undefined) {
    return '""';
  }
  const stringValue = String(field);
  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return `"${stringValue}"`;
}

/**
 * Exports a list of transactions to a CSV file and triggers browser download.
 * Adds UTF-8 BOM (\uFEFF) for 100% native compatibility with Microsoft Excel.
 */
export function exportLedgerToCsv(
  transactions: TransactionItem[],
  filename: string = `CKAudit_Statement_${new Date().toISOString().slice(0, 10)}.csv`
): void {
  const headers = [
    'Transaction ID',
    'Date',
    'Member',
    'Type',
    'Category',
    'Amount (INR)',
    'Description',
    'Receipt'
  ];

  const rows = transactions.map(tx => {
    const formattedDate = tx.transaction_date ? tx.transaction_date.slice(0, 10) : '';
    const formattedType = tx.transaction_type === 'receive' ? 'CREDIT (Received)' : 'DEBIT (Paid)';
    const signedAmount = tx.transaction_type === 'receive' ? `+${tx.amount.toFixed(2)}` : `-${tx.amount.toFixed(2)}`;
    const hasReceipt = tx.receipt_url ? 'Yes' : 'No';

    return [
      escapeCsvField(tx.id),
      escapeCsvField(formattedDate),
      escapeCsvField(tx.username || 'System'),
      escapeCsvField(formattedType),
      escapeCsvField(tx.category_name || 'General'),
      escapeCsvField(signedAmount),
      escapeCsvField(tx.description || ''),
      escapeCsvField(hasReceipt)
    ].join(',');
  });

  // Prepend UTF-8 Byte Order Mark (\uFEFF) so Excel treats it as UTF-8
  const csvContent = '\uFEFF' + [headers.map(escapeCsvField).join(','), ...rows].join('\r\n');

  // Trigger download via Blob
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
